import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import type { Vehicle } from '../../types';

interface AssignmentConfirmationModalProps {
  visible: boolean;
  vehicle: Vehicle | null;
  driverName: string;
  onCancel: () => void;
  onComplete: () => void;
}

const formatVehiclePlate = (vehicle: Vehicle): string => {
  const code = vehicle.vehicleCode || '1';
  const plate = vehicle.licensePlate || 'N/A';
  return `${code}-${plate}`;
};

export const AssignmentConfirmationModal: React.FC<AssignmentConfirmationModalProps> = ({
  visible,
  vehicle,
  driverName,
  onCancel,
  onComplete,
}) => {
  if (!vehicle) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerText}>⚠️ Complete Assignment First</Text>
          </View>

          {/* Content */}
          <View style={styles.content}>
            <Text style={styles.message}>
              Vehicle <Text style={styles.vehiclePlate}>{formatVehiclePlate(vehicle)}</Text> is currently assigned to{' '}
              <Text style={styles.driverName}>{driverName}</Text>.
            </Text>
            
            <Text style={styles.submessage}>
              You must complete the assignment and create a receiving form before opening a job card for this vehicle.
            </Text>
          </View>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={onCancel}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.completeButton]}
              onPress={onComplete}
            >
              <Text style={styles.completeButtonText}>Complete Assignment</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    ...Platform.select({
      web: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
      default: {
        elevation: 8,
      },
    }),
  },
  header: {
    backgroundColor: '#FFA500',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    padding: 16,
  },
  headerText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  content: {
    padding: 24,
  },
  message: {
    fontSize: 16,
    lineHeight: 24,
    color: '#333333',
    marginBottom: 16,
  },
  vehiclePlate: {
    fontWeight: '700',
    color: '#0066CC',
  },
  driverName: {
    fontWeight: '700',
    color: '#2E7D32',
  },
  submessage: {
    fontSize: 14,
    lineHeight: 20,
    color: '#666666',
  },
  buttonContainer: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    ...Platform.select({
      web: {
        justifyContent: 'flex-end',
        padding: 16,
        gap: 12,
      },
      default: {
        justifyContent: 'space-between',
      },
    }),
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  cancelButton: {
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    ...Platform.select({
      web: {
        marginRight: 0,
      },
      default: {
        flex: 1,
        marginRight: 8,
        marginLeft: 16,
        marginVertical: 12,
      },
    }),
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666666',
  },
  completeButton: {
    backgroundColor: '#0066CC',
    ...Platform.select({
      web: {
        marginLeft: 0,
      },
      default: {
        flex: 1,
        marginLeft: 8,
        marginRight: 16,
        marginVertical: 12,
      },
    }),
  },
  completeButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
